import { marketSearchApi } from '@envato/foundation-types'
import * as classnames from 'classnames'
import omit from 'lodash-es/omit'
import * as React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import 'rheostat/initialize'

import Rheostat from 'rheostat'

import PitLabel from './pitLabel'

import { ILocationParams } from '../../../../common/reducers/reactRouterLocation'
import pushHistory from '../../../../common/utils/location/pushHistory'

import * as styles from './filterMinResolution.module.css'

interface IProps extends RouteComponentProps<any> {
  paramName: string
  locationParams: ILocationParams
  items: marketSearchApi.IFilterOption[]
  loading: boolean
  makeSearchLink: (params: ILocationParams) => string
}

interface IState {
  indexSelected: number
  indexHovered: number
  dragged: boolean
}

interface ISliderChangePayload {
  values: number[]
}

interface IPitComponentProps {
  style: Record<string, unknown>
  children: number
}

interface IOption {
  key: string
  count: number
}

interface IHandleProps {
  handleRef: string
}

const SCALE = 3

const handle = ({ handleRef, ...passProps }: IHandleProps) => (
  <button
    type="button"
    ref={handleRef}
    aria-label={'minimum resolution slider'}
    {...passProps}
    className="resolutionHandle"
  />
)

class FilterMinResolution extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      indexSelected: this.fetchSelectedIndex(props),
      indexHovered: this.fetchSelectedIndex(props),
      dragged: false
    }
  }

  public render() {
    const { items } = this.props
    const { indexHovered } = this.state
    const snapPoints = items.map((v, index) => index * SCALE)
    return (
      <div className={styles.root}>
        <Rheostat
          snap
          onValuesUpdated={this.handleDrag}
          onChange={this.handleChange}
          max={(snapPoints.length - 1) * SCALE}
          snapPoints={snapPoints}
          pitPoints={snapPoints}
          pitComponent={this.createPitComponent}
          values={[indexHovered * SCALE]}
          handle={handle}
        />
      </div>
    )
  }

  private fetchSelectedIndex = (nextProps: IProps): number => {
    const { items, locationParams, paramName } = nextProps

    return items.indexOf(
      items.find(i => i.key === locationParams[paramName]) || items[0]
    )
  }

  private handleChange = ({
    values: [indexSelected]
  }: ISliderChangePayload) => {
    const {
      items,
      locationParams,
      history,
      makeSearchLink,
      paramName
    } = this.props

    const scaledIndex = indexSelected / SCALE

    const newQuery = omit(locationParams, ['page'])
    newQuery[paramName] = items[scaledIndex].key

    this.setState({ indexSelected: scaledIndex, dragged: false })

    pushHistory(history, `${makeSearchLink(newQuery)}#content`)
  }

  private handleDrag = ({ values: [index] }: ISliderChangePayload) => {
    const scaledIndex = index / SCALE

    if (this.state.indexHovered !== scaledIndex) {
      this.setState({ indexHovered: scaledIndex, dragged: true })
    }
  }

  private createPitComponent = ({ style, children }: IPitComponentProps) => {
    const { indexHovered, dragged } = this.state
    const { items, loading } = this.props
    const scaledChildren = children / SCALE

    const pitMarkerClassName = classnames(
      styles.pitMarker,
      scaledChildren >= indexHovered && styles.pitMarkerSelected
    )

    const showPitLabel = !dragged && !loading && scaledChildren === indexHovered

    return (
      <div style={style} className={styles.pitContainer}>
        {showPitLabel && <PitLabel count={items[indexHovered].count} />}
        <span className={styles.pitValue}>
          {items[scaledChildren].label || items[scaledChildren].key}
        </span>
        {<div className={pitMarkerClassName} />}
      </div>
    )
  }
}

export default withRouter(FilterMinResolution as any)
